import React from 'react';
import {useHistory} from 'react-router-dom';
import useSWR from 'swr';
import api, {getURL} from '../../api';
import {PATHS} from '../../Routes';
import {Lock, UnlockLink} from '../../utils/remoteAccess/types';
import {getURLSearchParam} from '../../utils/common';
import {useComputedReservationDetails} from '../../context/computedReservationDetails';
import Loader from '../../components/Loader';
import {NotAvailable, Success, Error} from './components';
import {LoaderWrapper, Wrapper} from './styled';

const UNLOCK_LINK_TOKEN_SEARCH_PARAM_NAME = 'unlock-link-token';

type UnlockResultData = {
  message: string;
  redirect_link: string | null;
  status_code: 500;
};

type UnlockResultError = {
  message: string;
  body: {
    message: string;
  };
};

enum StatusScreens {
  success = 'SUCCESS',
  notAvailable = 'NOT_AVAILABLE',
  error = 'ERROR',
}

function RemoteAccessStatusScreen() {
  const history = useHistory();
  const {reservation} = useComputedReservationDetails();
  const unlockLinkToken = getURLSearchParam(UNLOCK_LINK_TOKEN_SEARCH_PARAM_NAME);
  const [statusScreen, setStatusScreen] = React.useState<StatusScreens>(
    StatusScreens.notAvailable,
  );

  const {
    data: unlockLinkStatus,
    error,
    isValidating: isLoadingUnlock,
  } = useSWR<UnlockResultData>(
    unlockLinkToken && getURL(api.locks.ENDPOINTS.unlock(`token=${unlockLinkToken}`)),
    {
      revalidateIfStale: false,
      shouldRetryOnError: false,
    },
  );

  const needGetUnlockLinkDetails = unlockLinkToken && unlockLinkStatus;
  const {data: unlockLinksList, isValidating: isLoadingUnlockLinksList} = useSWR<
    UnlockLink[]
  >(
    needGetUnlockLinkDetails &&
      getURL(
        api.locks.ENDPOINTS.getOneUnlockLink(
          undefined,
          `reservation_id=${reservation.id}`,
        ),
      ),
    {revalidateIfStale: false, shouldRetryOnError: false},
  );

  const lockId = unlockLinksList?.find(
    unlockLinkDetails => unlockLinkDetails?.token === unlockLinkToken,
  )?.lock_id;

  const needGetLockDetails = lockId && unlockLinkStatus;
  const {data: lock, isValidating: isLoadingLock} = useSWR<Lock>(
    needGetLockDetails && getURL(api.locks.ENDPOINTS.getOneLock(lockId)),
    {revalidateIfStale: false, shouldRetryOnError: false},
  );

  const doorName = lock?.name || '';
  const isLoading = isLoadingUnlock || isLoadingUnlockLinksList || isLoadingLock;

  const handleStatusUnlockLink = React.useCallback(
    (data?: UnlockResultData, error?: UnlockResultError) => {
      if (data) {
        if (data.redirect_link) return (window.location.href = data.redirect_link);
        return setStatusScreen(StatusScreens.success);
      }
      if (error) {
        const isNotAvailable = error?.body?.message?.indexOf('date range') > 0;
        if (isNotAvailable) return setStatusScreen(StatusScreens.notAvailable);
        return setStatusScreen(StatusScreens.error);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (!unlockLinkToken) history.push(PATHS.home);
  }, [history, unlockLinkToken]);

  React.useEffect(() => {
    handleStatusUnlockLink(unlockLinkStatus, error);
  }, [unlockLinkStatus, error, handleStatusUnlockLink]);

  const StatusScreen = React.useMemo(() => {
    const screens = {
      [StatusScreens.success]: <Success doorName={doorName} />,
      [StatusScreens.notAvailable]: <NotAvailable />,
      [StatusScreens.error]: <Error />,
    } as {[key in StatusScreens]: React.ReactNode};

    return screens[statusScreen];
  }, [doorName, statusScreen]);

  return (
    <Wrapper>
      {isLoading && (
        <LoaderWrapper>
          <Loader width={200} />
        </LoaderWrapper>
      )}
      {!isLoading && StatusScreen}
    </Wrapper>
  );
}

export {RemoteAccessStatusScreen};
